// Base Rate — the minimum acceptable selling rate for a Product & Services master-data
// entry AFTER the line discount. One shared rule for every sales form (quotation,
// proforma): a line violates its Base Rate only when the effective unit selling rate
// falls strictly BELOW the Base Rate — exactly reaching it is allowed. A service with
// no Base Rate (0 or missing) has no minimum, and lines that don't match a master
// service are never checked.

import { isDurationUnit } from '@jira-clone/shared-types'
import { findMasterService, type MasterServiceRecord } from './serviceLines'

/** The smallest line shape any sales form needs for the Base Rate check. */
export interface BaseRateLine {
  name?: string
  quantity: number
  rate: number
  /** Unit of measure. A line billed by duration (Hours, Days, Weeks, Months, Years) is priced for the whole period. */
  unit?: string
  /** Quotation-style discount: a percent (discountType 'percent'/unset) or a fixed amount ('fixed'). */
  discount?: number
  discountType?: 'percent' | 'fixed'
  /** Proforma-style percent discount; takes precedence over `discount` when set. */
  discountPercent?: number
}

export interface BaseRateViolation<L extends BaseRateLine = BaseRateLine> {
  /** The offending line, so the form can highlight the exact row. */
  line: L
  /** The master-data service name the check resolved to. */
  name: string
  baseRate: number
  /** Unit selling rate after the line discount. */
  effectiveUnitRate: number
  /** How far the effective rate sits below the Base Rate. */
  difference: number
  /** What the user typed, for the warning text: "25%" or "₹500/unit". */
  discountLabel: string
}

// Money is compared unrounded (₹1.80 must not become ₹2); the epsilon only absorbs
// float noise from the percentage math so a discount that lands exactly on the
// Base Rate always passes.
const EPSILON = 1e-6

export const formatBaseRateAmount = (v: number): string =>
  Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** How many units the line's rate is spread over: 1 for a duration line, the quantity otherwise. */
const unitsPriced = (line: BaseRateLine): number => (isDurationUnit(line.unit) ? 1 : (Number(line.quantity) || 1))

/** The unit rate a line actually sells at, after its line discount. */
export function effectiveUnitRate(line: BaseRateLine): number {
  const qty = Number(line.quantity) || 0
  const rate = Number(line.rate) || 0
  if (qty <= 0) return 0
  if (line.discountType === 'fixed') {
    // A duration line's rate is the price for the whole period, so a fixed discount comes off it
    // as it is; for other lines it is spread across the units.
    return Math.max(0, rate - (Number(line.discount) || 0) / unitsPriced(line))
  }
  const pct = Number(line.discountPercent ?? line.discount ?? 0) || 0
  return Math.max(0, rate * (1 - pct / 100))
}

/** Checks one line against its master-data service's Base Rate; null when allowed. */
export function checkBaseRate<L extends BaseRateLine>(
  line: L,
  services: MasterServiceRecord[]
): BaseRateViolation<L> | null {
  const service = findMasterService(services, line.name ?? '')
  const baseRate = Number(service?.baseRate) || 0
  if (baseRate <= 0) return null
  // A line without a sellable quantity isn't priced at all (the forms require quantity > 0
  // before submission), so there is no selling rate to compare yet.
  if ((Number(line.quantity) || 0) <= 0) return null
  const effectiveRate = effectiveUnitRate(line)
  if (effectiveRate >= baseRate - EPSILON) return null
  const discountLabel =
    line.discountType === 'fixed'
      ? `₹${formatBaseRateAmount((Number(line.discount) || 0) / unitsPriced(line))}/${isDurationUnit(line.unit) ? 'period' : 'unit'}`
      : `${Number(line.discountPercent ?? line.discount ?? 0) || 0}%`
  return {
    line,
    name: service!.name,
    baseRate,
    effectiveUnitRate: effectiveRate,
    difference: baseRate - effectiveRate,
    discountLabel,
  }
}

/** Every line whose effective selling rate is below its master-data Base Rate. */
export function findBaseRateViolations<L extends BaseRateLine>(
  lines: L[] | undefined | null,
  services: MasterServiceRecord[]
): BaseRateViolation<L>[] {
  if (!Array.isArray(lines)) return []
  return lines
    .map((l) => checkBaseRate(l, services))
    .filter((v): v is BaseRateViolation<L> => v !== null)
}

/** The per-line warning text, naming the Base Rate and the calculated rate. */
export function baseRateWarningText(v: BaseRateViolation): string {
  return (
    `Below minimum Base Rate — minimum selling rate: ₹${formatBaseRateAmount(v.baseRate)}, ` +
    `rate after discount (${v.discountLabel}): ₹${formatBaseRateAmount(v.effectiveUnitRate)}. ` +
    `The applied discount takes this service below its minimum Base Rate.`
  )
}
